import React, { useEffect, useRef, useState } from 'react';
  import chevronLeftSmall from '../../../../public/assets/images/chevronleftsmall.svg';
  import datepickerIcon from '../../../../public/assets/images/calendarsingledatesolid.svg';
  import chevronRightSmall from '../../../../public/assets/images/chevronrightsmall.svg';
  import lockIcon from '../../../../public/assets/images/lockSolid.svg';
  import './datepicker.css';
  import {
    getTodayParts,
    getDaysInMonth,
    getFirstDayOfMonth,
    getMonthName,
    isValidCalendarDate,
    compareDateParts,
    parseFlexibleDateToMMDDYYYY,
  } from '../../../utils/date';

  interface Props {
    name: string;
    label?: string;
    value?: string;
    required?: boolean;
    helperText?: string;
    disabled?: boolean;
    error?: string;
    placeholder?: string;
    minDate?: Date; // Minimum allowed date
    maxDate?: Date; // Maximum allowed date
    /** When true, shows local range validation errors inline beneath the input. Defaults to false. */
    showInlineError?: boolean;
    onChange: (name: string, value: string) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void; // Added optional onBlur prop
    onValueBlur?: (name: string, value: string) => void;
  }

// Validates flexible date input (MM-DD-YYYY, MM/DD/YYYY, M-D-YY, M/D/YY, etc.)
// Accepts 1-digit month/day and 2-digit years for mid-typing tolerance.
const validateDateFormat = (v: string): boolean => {
  if (!v) return true;
  return parseFlexibleDateToMMDDYYYY(v) !== null;
};

// Normalizes flexible input to MM-DD-YYYY and extracts month/year for calendar view.
const parseValue = (v: string): { month: number; year: number } => {
  const canonical = parseFlexibleDateToMMDDYYYY(v);
  if (canonical) {
    const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(canonical);
    if (match) {
      return { month: Number.parseInt(match[1], 10) - 1, year: Number.parseInt(match[3], 10) };
    }
  }
  const today = getTodayParts();
  return { month: today.month, year: today.year };
};

/**
 * Checks whether a complete, format-valid date string falls within the minDate/maxDate range.
 * Returns a descriptive error message when out-of-range, or undefined when valid.
 */
const checkDateRange = (
  val: string,
  minDate: Date | undefined,
  maxDate: Date | undefined,
): string | undefined => {
  if (!val) return undefined;
  const match = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(val);
  if (!match) return undefined;
  const mm = Number.parseInt(match[1], 10);
  const dd = Number.parseInt(match[2], 10);
  const yy = Number.parseInt(match[3], 10);
  if (!isValidCalendarDate(yy, mm, dd)) return undefined;
  if (minDate && compareDateParts(yy, mm - 1, dd, minDate.getFullYear(), minDate.getMonth(), minDate.getDate()) < 0) {
    const mn = String(minDate.getMonth() + 1).padStart(2, '0');
    const md = String(minDate.getDate()).padStart(2, '0');
    return `Date must be on or after ${mn}-${md}-${minDate.getFullYear()}`;
  }
  if (maxDate && compareDateParts(yy, mm - 1, dd, maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate()) > 0) {
    const mn = String(maxDate.getMonth() + 1).padStart(2, '0');
    const md = String(maxDate.getDate()).padStart(2, '0');
    return `Date must be on or before ${mn}-${md}-${maxDate.getFullYear()}`;
  }
  return undefined;
};

// Static day-of-week header cells (computed once at module level).
const DAY_HEADER_CELLS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
  <div key={d} className="font-medium">{d}</div>
));

// Pads a number to 2 digits.
const pad2 = (n: number) => String(n).padStart(2, '0');
// Formats year/month(0-based)/day as MM-DD-YYYY.
const formatDDMMYYYY = (yy: number, mm: number, dd: number) => `${pad2(mm + 1)}-${pad2(dd)}-${yy}`;

  const CustomDatepicker: React.FC<Props> = ({ 
    name, 
    label, 
    value = '', 
    required, 
    helperText, 
    disabled, 
    error, 
    placeholder, 
    minDate, 
    maxDate, 
    showInlineError = false,
    onChange,
    onBlur,
    onValueBlur,
  }) => {

    const initial = parseValue(value);
    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState(initial.month);
    const [year, setYear] = useState(initial.year);
    const [formatError, setFormatError] = useState<boolean>(false);
    const [rangeError, setRangeError] = useState<string | undefined>(undefined);
    // Stays false until the field is blurred; reset to false whenever the user starts typing.
    const [showLocalError, setShowLocalError] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const calendarRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      const measure = () => {
        // Trigger a layout recalculation to ensure wrapper width is accurate when calendar opens
        requestAnimationFrame(() => {
          // We'll use wrapperRef directly when calendar opens
        });
      };

      // Run on mount and on resize
      measure();
      window.addEventListener('resize', measure);
      return () => {
        window.removeEventListener('resize', measure);
      };
    }, []);

    useEffect(() => {
      const onDocClick = (ev: MouseEvent) => {
        const t = ev.target as Node | null;
        if (!t || !open) return;
        if (wrapperRef.current?.contains(t)) return;
        setOpen(false);
      };
      document.addEventListener('click', onDocClick);
      return () => document.removeEventListener('click', onDocClick);
    }, [open]);

    useEffect(() => {
      if (!open) return undefined;
      const onKey = (ev: KeyboardEvent) => {
        if (ev.key === 'Escape') {
          setOpen(false);
          inputRef.current?.focus();
        }
        if (ev.key === 'ArrowLeft') {
          ev.preventDefault();
          changeMonth(-1);
        }
        if (ev.key === 'ArrowRight') {
          ev.preventDefault();
          changeMonth(1);
        }
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    useEffect(() => {
      const parsed = parseValue(value);
      setMonth(parsed.month);
      setYear(parsed.year);
      // Sync format and range errors when value changes externally (e.g. calendar selection)
      const isFormatValid = value.length === 0 || validateDateFormat(value);
      setFormatError(!isFormatValid);
      setRangeError(isFormatValid && value.length > 0 ? checkDateRange(value, minDate, maxDate) : undefined);
    }, [value, minDate, maxDate]);

    // Local errors (format + range) are only surfaced when showInlineError prop is true
    // AND the field has been blurred at least once (showLocalError=true).
    const localError: string | undefined = formatError ? 'Format MM-DD-YYYY' : rangeError;
    // Only surface local errors when showInlineError prop is true and the field has been blurred.
    let displayError: string | undefined = error;
    if (!displayError && showInlineError && showLocalError) {
      displayError = localError;
    }

    const changeMonth = (delta: number) => {
      let m = month + delta;
      let y = year;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      setMonth(m); setYear(y);
    };

    const firstDay = getFirstDayOfMonth(year, month);
    const daysInMonth = getDaysInMonth(year, month);
    // Build calendar cells including leading disabled days from previous month
    type Cell = { day: number; inMonth: boolean };
    const prevMonth = (month + 11) % 12;
    const prevMonthYear = year - Number(month === 0);
    const prevMonthLastDay = getDaysInMonth(prevMonthYear, prevMonth);
    const cells: Cell[] = [
      ...Array.from({ length: firstDay }, (_, i) => ({
        day: prevMonthLastDay - (firstDay - 1) + i,
        inMonth: false,
      })),
      ...Array.from({ length: daysInMonth }, (_, d) => ({
        day: d + 1,
        inMonth: true,
      })),
    ];

    // When calendar opens, measure and set the wrapper width so calendar matches input
    useEffect(() => {
      if (!open || !calendarRef.current || !wrapperRef.current) return;
      const measure = () => {
        requestAnimationFrame(() => {
          // Guard against null refs in test environments or after unmount
          if (!wrapperRef.current || !calendarRef.current) return;
          const wrapperWidth = Math.round((wrapperRef.current as HTMLElement).offsetWidth);
          if (wrapperWidth > 0) {
            calendarRef.current.style.width = `${wrapperWidth}px`;
            calendarRef.current.style.setProperty('--calendar-width', `${wrapperWidth}px`);
          }
        });
      };
      measure();
    }, [open]);

    return (
      <div className="ds-form" data-ds-theme="vessel" data-ds-variant="input" data-ds-version="1">
        {label && <label htmlFor={`date-input-${name}`} className={`${disabled ? " text-gray-500!" : ""}`}>{label}</label>}
        <div className="min-w-0! w-full date-field-wrapper" ref={wrapperRef}>
          <div className="input-with-icon">
            <input
              id={`date-input-${name}`}
              name={name}
              ref={inputRef}
              type="text"
              className={`min-w-0! block date-input-field  ${disabled ? "ds-form__disabled-field bg-gray-100 text-gray-600" : "bg-white!"} ${displayError ? 'field-error border-border-red! focus:outline-border-red!' : ''}`}
              value={value}
              onChange={(e) => {
                const val = e.target.value;
                const isFormatValid = val.length === 0 || validateDateFormat(val);
                setFormatError(!isFormatValid);
                setRangeError(isFormatValid && val.length > 0 ? checkDateRange(val, minDate, maxDate) : undefined);
                // Hide local error while the user is actively typing
                setShowLocalError(false);
                onChange(name, val);
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val) {
                  // Normalize any accepted flexible format to canonical MM-DD-YYYY on blur
                  const normalized = parseFlexibleDateToMMDDYYYY(val);
                  if (normalized) {
                    const isValid = validateDateFormat(normalized);
                    setFormatError(!isValid);
                    setRangeError(isValid ? checkDateRange(normalized, minDate, maxDate) : undefined);
                    if (normalized !== val) {
                      onChange(name, normalized);
                    }
                  }
                }
                // Reveal local error now that the user has left the field
                setShowLocalError(true);
                const normalizedValue = parseFlexibleDateToMMDDYYYY(val) ?? val;
                // Keep blur reporting separate from onChange so consumers can
                // defer validation and persistence until editing is complete.
                onValueBlur?.(name, normalizedValue);
                onBlur?.(e);
              }}
              onKeyDown={(e) => {
                // open calendar on Enter, Space or ArrowDown from input
                const k = e.key;
                if (k === 'Enter' || k === 'ArrowDown' || k === ' ' || k === 'Spacebar' || k === 'Space') {
                  e.preventDefault();
                  setOpen(true);
                }
              }}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
            />
            <button
              type="button"
              aria-label={disabled ? `Date field is locked for ${label ?? name}` : `Open calendar for ${label ?? name}`}
              onClick={() => !disabled && setOpen((s) => !s)}
              aria-expanded={open}
              disabled={disabled}
              className="date-input-icon"
            >
              <img src={disabled ? lockIcon : datepickerIcon} alt={disabled ? "locked" : "calendar"} />
            </button>
          </div>

          {open && !disabled && (
            <dialog
              id={`custom-calendar-${name}`}
              ref={calendarRef as React.RefObject<HTMLDialogElement>}
              className="custom-calendar bg-white border rounded shadow-lg p-3 absolute top-12 left-0"
              aria-label={`Calendar for ${label ?? name}`}
              open={open}
            >
              <div className="cal-header flex justify-between items-center mb-2">
                <button
                  type="button"
                  onClick={() => changeMonth(-1)}
                  aria-label="Previous month"
                  disabled={!!minDate && compareDateParts(year, month, 1, minDate.getFullYear(), minDate.getMonth(), 1) <= 0}
                  className={`p-1${!!minDate && compareDateParts(year, month, 1, minDate.getFullYear(), minDate.getMonth(), 1) <= 0 ? ' opacity-30 cursor-not-allowed' : ''}`}
                >
                  <img src={chevronLeftSmall} alt="Previous month" className="w-4 h-4" />
                </button>
                <div>{getMonthName(month)} {year}</div>
                <button
                  type="button"
                  onClick={() => changeMonth(1)}
                  aria-label="Next month"
                  disabled={!!maxDate && compareDateParts(year, month, 1, maxDate.getFullYear(), maxDate.getMonth(), 1) >= 0}
                  className={`p-1${!!maxDate && compareDateParts(year, month, 1, maxDate.getFullYear(), maxDate.getMonth(), 1) >= 0 ? ' opacity-30 cursor-not-allowed' : ''}`}
                >
                  <img src={chevronRightSmall} alt="Next month" className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-sm">
                {DAY_HEADER_CELLS}
                {cells.map((c) => {
                  // c: { day: number; inMonth: boolean }
                  const uniqueKey = c.inMonth ? `${year}-${month}-${c.day}` : `disabled-${year}-${month}-${c.day}`;
                  
                  if (!c.inMonth) {
                    return (
                      <div key={uniqueKey} className="day">
                        <div className="disabled-day" aria-hidden="true">{c.day}</div>
                      </div>
                    );
                  }

                  const dayVal = formatDDMMYYYY(year, month, c.day);
                  const isSelected = value === dayVal;

                  // Check if date is within valid range (pure numeric comparison, no new Date)
                  let isDisabled = false;
                  if (minDate) {
                    if (compareDateParts(year, month, c.day, minDate.getFullYear(), minDate.getMonth(), minDate.getDate()) < 0) isDisabled = true;
                  }
                  if (maxDate) {
                    if (compareDateParts(year, month, c.day, maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate()) > 0) isDisabled = true;
                  }

                  // Out-of-range days use the same disabled-day class as out-of-month days
                  if (isDisabled) {
                    return (
                      <div key={uniqueKey} className="day">
                        <div className="disabled-day" aria-hidden="true">{c.day}</div>
                      </div>
                    );
                  }

                  const buttonClass = `w-9 h-9 rounded-full flex items-center justify-center ${isSelected ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'}`;

                  return (
                    <div key={uniqueKey} className="day cursor-pointer">
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        className={buttonClass}
                        onClick={() => {
                          const currentValue = parseFlexibleDateToMMDDYYYY(value) ?? value;
                          onChange(name, dayVal);
                          // Calendar selection is already a completed edit; report
                          // only a genuinely different date to avoid duplicate saves.
                          if (currentValue !== dayVal) {
                            onValueBlur?.(name, dayVal);
                          }
                          setOpen(false);
                        }}
                      >
                        {c.day}
                      </button>
                    </div>
                  );
                })}
              </div>
            </dialog>
          )}

        </div>
        {displayError ? <div className="error-description">
    <ul id="text-input-error-description-list-1">
      <li>
        <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
        <span className="screenreader-only">Error</span>
        {displayError}
      </li>
    </ul>
  </div> : <span className="ds-form__text-input-helper">{helperText}</span>}
      </div>
    );
  };

  export default CustomDatepicker;
